/**
 * W5 — Command palette resolver
 * WB4-CP1 — 6-group operating layer (Go / Act / Filter / Flow / Create / Explain)
 */

import type { PathPlane } from '@shared/tenant-host/pathPlanes';
import { omitUndefined } from '@shared/utils/omitUndefined';
import type { UniversalBaseEntity } from '@shared/types/entity';
import type { UserRole } from '@shared/types/roles';
import { projectActions } from './action-projections';
import { filterCommands, type CommandEntry, type CommandKind } from './command-registry';
import {
  applyPaletteTopActionPinPolicy,
  buildExplainPaletteCommands,
  buildExplainPaletteHandlerRef,
  partitionCommandsByPaletteGroup,
  resolveKernelActionPaletteGroup,
  resolveRegistryCommandPaletteGroup,
  resolveResolvedCommandPaletteGroup,
  type CommandPaletteGroup,
  type PaletteGroupedCommands,
} from './workbench-command-palette-contract';
import { isManifestKernelHandlerRefDispatchable } from './domain-kernel-handler-coverage';
import type { WorkbenchFocus } from './workbench-focus';
import { resolveFocusIntent, resolveWorkbenchActions } from './workbench-kernel';

export type CommandSection = 'route' | 'action';

export type { CommandPaletteGroup, PaletteGroupedCommands };

export interface WorkbenchCommandContext {
  role: UserRole;
  plane: PathPlane;
  permissionKeys: string[];
  object?: UniversalBaseEntity;
  businessDomain?: string;
  status?: string;
  /** WB3 focus bridge — intent + selection for kernel v2 / CR4 command palette */
  focus?: WorkbenchFocus;
}

export interface ResolvedCommand {
  id: string;
  label: string;
  section: CommandSection;
  kind: CommandKind;
  group?: CommandPaletteGroup;
  route?: string;
  actionRef?: string;
  handlerRef?: string;
  priority: number;
  disabled?: boolean;
  reason?: string;
}

function resolveHandlerRef(
  command: Pick<ResolvedCommand, 'handlerRef' | 'actionRef'>
): string | undefined {
  return command.handlerRef ?? command.actionRef;
}

function registryToResolved(entry: CommandEntry): ResolvedCommand {
  const group = entry.kind === 'explain' ? 'explain' : resolveRegistryCommandPaletteGroup(entry);
  const handlerRef =
    entry.kind === 'explain' && entry.sourceActionId
      ? buildExplainPaletteHandlerRef(entry.sourceActionId)
      : undefined;
  const section: CommandSection = group === 'go' ? 'route' : 'action';
  return omitUndefined({
    id: entry.id,
    label: entry.label,
    section,
    kind: entry.kind,
    group,
    route: entry.route,
    actionRef: entry.actionRef,
    handlerRef,
    priority: entry.priority ?? 0,
    reason:
      entry.kind === 'explain' && entry.sourceActionId ? entry.keywords?.join(' · ') : undefined,
  }) as ResolvedCommand;
}

function resolveActiveBusinessDomain(ctx: WorkbenchCommandContext): string | undefined {
  return (
    ctx.businessDomain ??
    ctx.focus?.root.businessDomain ??
    (ctx.object?.businessDomain !== undefined ? String(ctx.object.businessDomain) : undefined)
  );
}

function buildFocusExplainPaletteCommands(
  ctx: WorkbenchCommandContext,
  source: readonly ResolvedCommand[]
): ResolvedCommand[] {
  const domain = resolveActiveBusinessDomain(ctx);
  if (!domain || (!ctx.object && !ctx.focus?.root.id)) {
    return buildExplainPaletteCommands(source);
  }
  const objectLabel =
    ctx.focus?.root.label ?? ctx.object?.name ?? ctx.focus?.root.id ?? '선택 객체';
  const proactiveExplain: ResolvedCommand = {
    id: `explain:focus-blocked:${domain}`,
    label: `"${objectLabel}" — 차단된 작업 설명`,
    section: 'action',
    kind: 'explain',
    group: 'explain',
    handlerRef: buildExplainPaletteHandlerRef(`focus-blocked-${domain}`),
    priority: 42,
    reason: `${objectLabel}(${domain}) 기준으로 실행 불가 사유를 설명합니다.`,
  };
  return dedupeById([proactiveExplain, ...buildExplainPaletteCommands(source)]);
}

function matchesQuery(label: string, query: string): boolean {
  if (!query) {
    return true;
  }
  return label.toLowerCase().includes(query.toLowerCase());
}

function dedupeById(commands: ResolvedCommand[]): ResolvedCommand[] {
  const seen = new Set<string>();
  const result: ResolvedCommand[] = [];
  for (const command of commands) {
    if (seen.has(command.id)) {
      continue;
    }
    seen.add(command.id);
    result.push(command);
  }
  return result;
}

function annotateUndispatchableCommands(commands: ResolvedCommand[]): ResolvedCommand[] {
  return commands.map((command) => {
    const handlerRef = resolveHandlerRef(command);
    if (command.route || !handlerRef || command.disabled) {
      return command;
    }
    if (isManifestKernelHandlerRefDispatchable(handlerRef)) {
      return command;
    }
    return {
      ...command,
      disabled: true,
      reason: command.reason?.trim() || '아직 연결되지 않은 명령입니다',
    };
  });
}

export function resolveCommands(ctx: WorkbenchCommandContext, query = ''): ResolvedCommand[] {
  const normalized = query.trim().toLowerCase();

  const activeBusinessDomain = resolveActiveBusinessDomain(ctx);

  const registryMatches = filterCommands({
    query,
    plane: ctx.plane,
    role: ctx.role,
    ...(activeBusinessDomain !== undefined ? { activeBusinessDomain } : {}),
  });

  const registryCommands = registryMatches.map((entry) => registryToResolved(entry));

  let resolverActions: ResolvedCommand[] = [];
  if (ctx.object) {
    const businessDomain = ctx.businessDomain ?? String(ctx.object.businessDomain ?? 'entity');
    const status = ctx.status ?? String(ctx.object.status);
    const focus: WorkbenchFocus = ctx.focus ?? {
      root: {
        id: ctx.object.id,
        businessDomain,
        ...(ctx.object.name ? { label: ctx.object.name } : {}),
      },
      selection: [],
      intent: resolveFocusIntent({
        role: ctx.role,
        status,
        plane: ctx.plane,
        permissionKeys: ctx.permissionKeys,
      }),
      mode: 'view',
      variant: 'object',
      actionProjection: 'commandPalette',
    };

    const actions = resolveWorkbenchActions(
      ctx.object,
      {
        role: ctx.role,
        plane: ctx.plane,
        permissionKeys: ctx.permissionKeys,
        surface: 'command',
        businessDomain,
        status,
        focus,
      },
      { permissionKeys: ctx.permissionKeys }
    );

    resolverActions = projectActions(actions, 'command')
      .commandPalette.filter((action) => action.label.length > 0)
      .filter((action) => matchesQuery(action.label, normalized))
      .map((action) => ({
        id: action.id,
        label: action.label,
        section: 'action' as const,
        kind: 'action' as const,
        group: resolveKernelActionPaletteGroup(action),
        handlerRef: action.handlerRef,
        priority: action.priority,
        ...(action.availability === 'disabled' || action.forbidden ? { disabled: true } : {}),
        ...(action.reason?.trim() ? { reason: action.reason.trim() } : {}),
      }));
  }

  return annotateUndispatchableCommands(
    dedupeById([...registryCommands, ...resolverActions]).sort((a, b) => b.priority - a.priority)
  );
}

export function resolvePaletteGroupedCommands(
  ctx: WorkbenchCommandContext,
  query = ''
): PaletteGroupedCommands {
  const resolved = resolveCommands(ctx, query);
  const explainCommands = buildFocusExplainPaletteCommands(ctx, resolved);
  const merged = dedupeById([...resolved, ...explainCommands]);
  const pinned = applyPaletteTopActionPinPolicy(merged, {
    query,
    hasObjectContext: Boolean(ctx.object),
  });
  return partitionCommandsByPaletteGroup(pinned);
}

export function partitionCommandsBySection(commands: ResolvedCommand[]): {
  route: ResolvedCommand[];
  action: ResolvedCommand[];
} {
  return {
    route: commands.filter((command) => resolveResolvedCommandPaletteGroup(command) === 'go'),
    action: commands.filter((command) => resolveResolvedCommandPaletteGroup(command) !== 'go'),
  };
}
